import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { createTheme } from "@mui/material/styles";

// The app theme (theme/createAppTheme) keeps MUI's default breakpoints.
const MD_BREAKPOINT = createTheme().breakpoints.values.md; // 900

/**
 * Mobile mode rule, shared by the initial seed here, App.js's resize
 * listener and toastHelper. Strictly below `md`, the same rule as the CSS
 * `theme.breakpoints.down("md")` (max-width: 899.95px) that shows the bottom
 * nav and hides the navbar, so JS and CSS switch at the same pixel.
 */
export const isMobileWidth = (width, md = MD_BREAKPOINT) => width < md;

// Viewport/responsive UI state. Replaced the Redux `ui` domain; semantics
// ported verbatim: isMobile is seeded at load time from the window width
// against the Material-UI 'md' breakpoint (isMobileWidth), and kept up to date by
// App.js's window resize listener calling setIsMobile.
//
// mobilePanel / mobileSnap are the single source of truth for which panel is
// open on phones ("area" | "layers" | "statistics" | null) and how far its
// bottom sheet is pulled up ("peek" | "half" | "full").
// hooks/useMobilePanelSync mirrors them into the legacy
// DashboardStore.mainMenuOpen and StatisticsStore flags, and back when other
// code opens statistics.
//
// insightsRequested: phones, "Analyze this area" (openInsights) asks the
// Analytics panel's Insights list (StatisticsMenu's script picker) to open by
// itself. One-shot: the list clears it when it opens or finds nothing to
// list, and any other panel change (setMobilePanel) drops it.
const useUiStore = create(
  devtools(
    (set) => ({
      isMobile: isMobileWidth(window.innerWidth), // Set initial state based on window size
      setIsMobile: (isMobile) => set({ isMobile }, false, "ui/setIsMobile"),

      mobilePanel: null,
      mobileSnap: "peek",
      setMobilePanel: (panel, snap = "half") =>
        set(
          {
            mobilePanel: panel,
            mobileSnap: panel ? snap : "peek",
            insightsRequested: false,
          },
          false,
          "ui/setMobilePanel",
        ),
      insightsRequested: false,
      // At full, so the list has room below the field.
      openInsights: () =>
        set(
          {
            mobilePanel: "statistics",
            mobileSnap: "full",
            insightsRequested: true,
          },
          false,
          "ui/openInsights",
        ),
      clearInsightsRequest: () =>
        set({ insightsRequested: false }, false, "ui/clearInsightsRequest"),
      setMobileSnap: (snap) =>
        set({ mobileSnap: snap }, false, "ui/setMobileSnap"),
    }),
    { name: "ui" },
  ),
);

export default useUiStore;
